import { autoUpdate, computePosition, flip, hide, size } from "@floating-ui/dom";
import { attr, observable, Observable, Updates, volatile } from '@microsoft/fast-element';
import { angleDown } from '../../styles/icons';
import { NatGenTextField, NatGenTextFieldAppearance } from '../text-field/text-field';
import { comboboxStyles } from './combobox.styles';
import { ComboboxAutocomplete } from './combobox.options';
import { NatGenOption } from '../option/option';
import { FormAssociated } from '../../foundation/form-associated';
import { DelegatesARIAListbox, NatGenListbox } from '../listbox/listbox';
import { limit, uniqueId } from '@microsoft/fast-web-utilities';
import { applyMixins } from '../../foundation/apply-mixins';
import { StartEnd } from '../start-end/start-end';
import { getRootActiveElement } from "../../foundation/random";
import { comboboxTemplate } from "./combobox.template";
import { NatGenDesignSystem } from "../../natgen-design-system";

class _Combobox extends NatGenListbox {}
interface _Combobox extends FormAssociated {}

/**
 * A form-associated base class for the {@link (Combobox:class)} component.
 *
 * @beta
 */
export class FormAssociatedCombobox extends FormAssociated(_Combobox) {
    proxy = document.createElement("input");
}

/**
 * A Combobox Custom HTML Element.
 * Implements the {@link https://w3c.github.io/aria-practices/#combobox | ARIA combobox }.
 *
 * @element natgen-combobox
 * @slot start - Content which can be provided before the input
 * @slot end - Content which can be provided after the input
 * @slot control - Used to replace the input element representing the combobox
 * @slot indicator - The visual indicator representing the expanded state
 * @slot - The default slot for the options
 * @csspart control - The wrapper element containing the input area, including start and end
 * @csspart selected-value - The input element representing the selected value
 * @csspart indicator - The element wrapping the indicator slot
 * @csspart listbox - The wrapper for the listbox slotted options
 * @fires change - Fires a custom 'change' event when the value updates
 *
 * @public
 */
export class NatGenCombobox extends FormAssociatedCombobox {
    private static readonly freeformValidationMessage = "Please select a value from the list.";
    private hasFocus: boolean = false;

    /**
     * The appearance of the element.
     *
     * @public
     * @remarks
     * HTML Attribute: appearance
     */
    @attr
    public appearance: NatGenTextFieldAppearance;

    public appearanceChanged(
        oldValue: NatGenTextFieldAppearance,
        newValue: NatGenTextFieldAppearance
    ): void {
        if (oldValue !== newValue) {
            this.classList.add(newValue);
            this.classList.remove(oldValue);
        }
    }

    /**
     * The internal value property.
     *
     * @internal
     */
    private _value: string = "";

    /**
     * The autocomplete attribute.
     *
     * @public
     * @remarks
     * HTML Attribute: autocomplete
     */
    @attr({ attribute: 'autocomplete', mode: 'fromView' })
    autocomplete: ComboboxAutocomplete | undefined = 'list';

    /**
     * Allows values that do not exactly match an existing option.
     *
     * @public
     * @remarks
     * HTML Attribute: accept-freeform
     */
    @attr({ attribute: 'accept-freeform', mode: 'boolean' })
    public acceptFreeform: boolean = false;

    public acceptFreeformChanged(): void {
        this.updateFreeformValidityState();
    }

    /**
     * Reference to the internal text input element.
     *
     * @internal
     */
    public control: NatGenTextField;

    /**
     * Reference to the internal listbox element.
     *
     * @internal
     */
    public listbox: HTMLDivElement;

    /**
     * The collection of currently filtered options.
     *
     * @public
     */
    public filteredOptions: NatGenOption[] = [];

    /**
     * The current filter value.
     *
     * @internal
     */
    private filter: string = "";

    /**
     * Stores a value requested before matching options are available.
     */
    private pendingSlottedValue: string = "";

    /**
     * Reset the element to its first selectable option when its parent form is reset.
     *
     * @internal
     */
    public formResetCallback(): void {
        super.formResetCallback();
        this.setDefaultSelectedOption();

        if (!this.firstSelectedOption) {
            this.applyValue(this.initialValue ?? "", false);
            return;
        }

        this.updateValue();
    }

    /** {@inheritDoc (FormAssociated:interface).validate} */
    public validate(): void {
        const anchor = this.control;
        const isValidAnchor = !!anchor && !!this.shadowRoot?.contains(anchor);
        super.validate(isValidAnchor ? anchor : undefined);
    }

    private get isAutocompleteInline(): boolean {
        return (
            this.autocomplete === ComboboxAutocomplete.inline || this.isAutocompleteBoth
        );
    }

    private get isAutocompleteList(): boolean {
        return this.autocomplete === ComboboxAutocomplete.list || this.isAutocompleteBoth;
    }

    private get isAutocompleteBoth(): boolean {
        return this.autocomplete === ComboboxAutocomplete.both;
    }

    /**
     * The unique id for the internal listbox element.
     *
     * @internal
     */
    public listboxId: string = uniqueId("listbox-");

    /**
     * The open attribute.
     *
     * @public
     * @remarks
     * HTML Attribute: open
     */
    @attr({ attribute: "open", mode: "boolean" })
    public open: boolean = false;
    
    /**
     * The component is collapsible when in single-selection mode unless no-collapse is specified.
     *
     * @internal
     */
    @volatile
    public get collapsible(): boolean {
        return !this.noCollapse;
    }

    @attr({attribute: 'no-collapse', mode: 'boolean'})
    public noCollapse: boolean;

    /**
     * Sets focus and synchronize ARIA attributes when the open property changes.
     *
     * @param prev - the previous open value
     * @param next - the current open value
     *
     * @internal
     */
    protected openChanged() {
        if (this.open) {
            this.ariaControls = this.listboxId;
            this.ariaExpanded = "true";

            Updates.enqueue(() => this.setPositioning());
            this.focusAndScrollOptionIntoView();

            // focus is directed to the element when `open` is changed programmatically
            //Updates.enqueue(() => this.focus());

            return;
        }

        this.ariaControls = "";
        this.ariaExpanded = "false";
    }

    /**
     * The list of options.
     *
     * @public
     * @remarks
     * Overrides `Listbox.options`.
     */
    public get options(): NatGenOption[] {
        Observable.track(this, "options");
        return this.filteredOptions.length ? this.filteredOptions : this._options;
    }

    public set options(value: NatGenOption[]) {
        this._options = value;
        Observable.notify(this, "options");
    }

    /**
     * Sets the placeholder value of the element, generally used to provide a hint to the user.
     * @public
     * @remarks
     * HTML Attribute: placeholder
     * Using this attribute is not a valid substitute for a labeling element.
     */
    @attr
    public placeholder: string;

    /**
     * Updates the placeholder on the proxy element.
     * @internal
     */
    protected placeholderChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.placeholder = this.placeholder;
        }
    }

    /**
     * The value property.
     *
     * @public
     */
    public get value() {
        Observable.track(this, "value");
        return this._value;
    }

    public set value(next: string) {
        this.applyValue(next, true);
    }

    private applyValue(next: string, emitChange: boolean, preserveUnmatched: boolean = false): void {
        const prev = `${this._value}`;

        if (this.$fastController.isConnected && this.options) {
            const matchingOption = this.findOptionByValue(next);
            const prevSelectedValue = this.firstSelectedOption?.value;
            const nextSelectedValue = matchingOption?.value;

            if (!preserveUnmatched && !this.acceptFreeform && next && !matchingOption) {
                next = "";
            }

            if (prevSelectedValue !== nextSelectedValue) {
                if (matchingOption) {
                    if (this.filteredOptions.length && !this.filteredOptions.includes(matchingOption)) {
                        this.filteredOptions = [];
                        this._options.forEach(option => {
                            option.hidden = false;
                        });
                    }

                    this.selectedOptions = [matchingOption];
                    this.selectedIndex = this.options.findIndex(option => option === matchingOption);
                } else {
                    this.selectedIndex = -1;
                    this.selectedOptions = [];
                }
            }

            next = matchingOption?.value ?? next;
        }

        if (prev !== next) {
            this._value = next;
            super.valueChanged(prev, next);
            Observable.notify(this, "value");
            Observable.notify(this, "displayValue");

            if (this.$fastController.isConnected && emitChange) {
                this.$emit("change");
            }
        }

        this.updateFreeformValidityState();
    }

    public get displayValue(): string {
        Observable.track(this, "displayValue");

        if (this.firstSelectedOption) {
            return this.firstSelectedOption.text;
        }

        return this.acceptFreeform ? this.value ?? "" : "";
    }

    /**
     * Invoked when currentValue changes - triggers filtering.
     *
     * @internal
     */
    protected currentValueChanged(prev: string, next: string): void {
        const current = next ?? "";
        this.pendingSlottedValue = current;

        this.applyValue(current, false, true);

        if (this.currentValue !== this.value) {
            this.currentValue = this.value;
            return;
        }

        if (this.control && this.$fastController.isConnected) {
            this.control.value = this.displayValue;
            this.filter = this.displayValue;
            this.filterOptions();
            this.updateFreeformValidityState();
        }
    }

    /**
     * Cleanup function for the listbox positioner.
     *
     * @public
     */
    public cleanup: () => void;

    /**
     * Handle opening and closing the listbox when the combobox is clicked.
     *
     * @param e - the mouse event
     * @internal
     */
    public clickHandler(e: MouseEvent): boolean | void {
        const captured = (e.target as HTMLElement).closest(
            `option,[role=option]`
        ) as NatGenOption | null;

        if (this.disabled || captured?.disabled) {
            return;
        }

        if (this.open) {
            if (e.composedPath()[0] === this.control) {
                return;
            }

            if (captured) {
                this.selectedOptions = [captured];
                this.control.value = captured.text;
                this.clearSelectionRange();
                this.updateValue(true);
            }
        }

        this.open = !this.open;

        if (this.open) {
            this.control.focus();
        }

        return true;
    }

    public connectedCallback() {
        super.connectedCallback();
        if (this.value) {
            this.initialValue = this.value;
        }
        this.updateFreeformValidityState();
    }

    /**
     * Synchronize the `aria-disabled` property when the `disabled` property changes.
     *
     * @param prev - The previous disabled value
     * @param next - The next disabled value
     *
     * @internal
     */
    public disabledChanged(prev: boolean, next: boolean): void {
        if (super.disabledChanged) {
            super.disabledChanged(prev, next);
        }
        this.ariaDisabled = this.disabled ? "true" : "false";
    }

    public disconnectedCallback(): void {
        this.cleanup?.();
        super.disconnectedCallback();
    }

    /**
     * Filter available options by text value.
     *
     * @public
     */
    public filterOptions(): void {
        if (!this.autocomplete || this.autocomplete === ComboboxAutocomplete.none) {
            this.filter = "";
        }

        const filter = this.filter.toLowerCase();

        this.filteredOptions = this._options.filter(o =>
            o.text.toLowerCase().includes(this.filter.toLowerCase())
        );

        if (this.isAutocompleteList) {
            if (!this.filteredOptions.length && !filter) {
                this.filteredOptions = this._options;
            }

            this._options.forEach(o => {
                o.hidden = !this.filteredOptions.includes(o);
            });
        }
    }

    /**
     * Focus the control and scroll the first selected option into view.
     *
     * @internal
     * @remarks
     * Overrides: `Listbox.focusAndScrollOptionIntoView`
     */
    protected focusAndScrollOptionIntoView(): void {
        if (this.contains(getRootActiveElement(this))) {
            this.control.focus();
            if (this.firstSelectedOption) {
                requestAnimationFrame(() => {
                    this.firstSelectedOption?.scrollIntoView({ block: "nearest" });
                });
            }
        }
    }

    /**
     * Handle focus state when the element or its children lose focus.
     *
     * @param e - The focus event
     * @internal
     */
    public focusoutHandler(e: FocusEvent): boolean | void {
        this.hasFocus = false;
        this.syncValue();

        if (!this.open) {
            return true;
        }

        const focusTarget = e.relatedTarget as HTMLElement;
        if (this.isSameNode(focusTarget)) {
            this.focus();
            return;
        }

        if (!this.options || !this.options.includes(focusTarget as NatGenOption)) {
            this.open = false;
        }
    }

    /**
     * Handle focus state when the element or its children receive focus.
     *
     * @param e - The focus event
     * @internal
     */
    public focusinHandler(e: FocusEvent): void {
        this.hasFocus = true;
        this.updateFreeformValidityState();
        super.focusinHandler(e);
    }

    /**
     * Handle content changes on the control input.
     *
     * @param e - the input event
     * @internal
     */
    public inputHandler(e: InputEvent): boolean | void {
        this.filter = this.control.value;
        this.filterOptions();

        if (!this.isAutocompleteInline) {
            this.selectedIndex = this.options
                .map(option => option.text)
                .indexOf(this.control.value);
        }

        if (e.inputType.includes("deleteContent") || !this.filter.length) {
            return true;
        }

        if (this.isAutocompleteList && !this.open) {
            this.open = true;
        }

        if (this.isAutocompleteInline) {
            if (this.filteredOptions.length) {
                this.selectedOptions = [this.filteredOptions[0]];
                this.selectedIndex = this.options.indexOf(this.firstSelectedOption);
                this.setInlineSelection();
            } else {
                this.selectedIndex = -1;
            }
        }

        this.updateFreeformValidityState();
        return;
    }

    /**
     * Handle keydown actions for listbox navigation.
     *
     * @param e - the keyboard event
     * @internal
     */
    public keydownHandler(e: Event & KeyboardEvent): boolean | void {
        const key = e.key;

        if (e.ctrlKey || e.shiftKey) {
            return true;
        }

        switch (key) {
            case "Enter": {
                this.commitSelection();
                if (this.isAutocompleteInline) {
                    this.filter = this.displayValue;
                }

                this.open = false;
                this.clearSelectionRange();
                break;
            }

            case "Escape": {
                if (!this.isAutocompleteInline) {
                    this.selectedIndex = -1;
                }

                if (this.open) {
                    this.open = false;
                    break;
                }

                this.applyValue("", false);
                this.control.value = "";
                this.filter = "";
                this.filterOptions();
                break;
            }

            case "Tab": {
                this.commitSelection();

                if (!this.open) {
                    return true;
                }

                e.preventDefault();
                this.open = false;
                break;
            }

            case "ArrowUp":
            case "ArrowDown": {
                this.filterOptions();

                if (!this.open) {
                    this.open = true;
                    break;
                }

                if (this.filteredOptions.length > 0) {
                    super.keydownHandler(e);
                }

                if (this.isAutocompleteInline) {
                    this.setInlineSelection();
                }

                break;
            }

            default: {
                return true;
            }
        }
    }

    /**
     * Handle keyup actions for value input and text field manipulations.
     *
     * @param e - the keyboard event
     * @internal
     */
    public keyupHandler(e: KeyboardEvent): boolean | void {
        const key = e.key;

        switch (key) {
            case "ArrowLeft":
            case "ArrowRight":
            case "Backspace":
            case "Delete":
            case "Home":
            case "End": {
                this.filter = this.control.value;
                this.selectedIndex = -1;
                this.filterOptions();
                break;
            }
        }
    }

    /**
     * Ensure that the selectedIndex is within the current allowable filtered range.
     *
     * @param prev - the previous selected index value
     * @param next - the current selected index value
     *
     * @internal
     */
    public selectedIndexChanged(prev: number | undefined, next: number): void {
        if (this.$fastController.isConnected) {
            next = limit(-1, this.options.length - 1, next);

            // we only want to call the super method when the selectedIndex is in range
            if (next !== this.selectedIndex) {
                this.selectedIndex = next;
                return;
            }

            super.selectedIndexChanged(prev, next);
        }
    }

    /**
     * Move focus to the previous selectable option.
     *
     * @internal
     * @remarks
     * Overrides `Listbox.selectPreviousOption`
     */
    public selectPreviousOption(): void {
        if (!this.disabled && this.selectedIndex >= 0) {
            this.selectedIndex = this.selectedIndex - 1;
        }
    }

    /**
     * Set the default selected options at initialization or reset.
     *
     * @internal
     * @remarks
     * Overrides `Listbox.setDefaultSelectedOption`
     */
    public setDefaultSelectedOption(): void {
        if (this.$fastController.isConnected && this.options) {
            const requestedValue = this.pendingSlottedValue || this.currentValue || this.getAttribute("current-value") || this.value;
            const selectedIndex = this.options.findIndex(
                el => el.getAttribute("selected") !== null || el.selected || el.value === requestedValue
            );

            this.selectedIndex = selectedIndex;
            if (this.firstSelectedOption) {
                const hadPending = this.pendingSlottedValue !== "";
                this.applyValue(this.firstSelectedOption.value, false);
                this.pendingSlottedValue = "";

                if (hadPending) {
                    this.$emit("change");
                }
            } else if (!this.acceptFreeform && this.options.length === 0) {
                this.applyValue("", false);
                this.pendingSlottedValue = "";
            } else if (requestedValue) {
                this.applyValue(requestedValue, false, true);
                this.pendingSlottedValue = requestedValue;
            } else if (!this.dirtyValue && this.value === "") {
                this.applyValue("", false);
            }

            this.setSelectedOptions();
        }
    }

    /**
     * Focus and set the content of the control based on the first selected option.
     *
     * @internal
     */
    private setInputToSelection(): void {
        if (this.firstSelectedOption) {
            this.control.value = this.firstSelectedOption.text;
            this.control.focus();
        }
    }

    /**
     * Focus, set and select the content of the control based on the first selected option.
     *
     * @internal
     */
    private setInlineSelection(): void {
        if (this.firstSelectedOption) {
            this.setInputToSelection();
            this.control.control.setSelectionRange(
                this.filter.length,
                this.control.value.length,
                "backward"
            );
        }
    }

    /**
     * Commits the current selection or top filtered option.
     *
     * @internal
     */
    private commitSelection(): void {
        const typedValue = this.control.value;

        if (!this.acceptFreeform) {
            const matchingOption = this.findMatchingOption(typedValue);
            const topFilteredOption = this.filteredOptions[0];

            if (!typedValue.trim().length) {
                this.selectedIndex = -1;
                this.applyValue("", false);
                this.updateValue(this.value !== "");
                return;
            }

            if (matchingOption) {
                this.selectedOptions = [matchingOption];
                this.selectedIndex = this.options.indexOf(matchingOption);
                this.control.value = matchingOption.text;
                this.filter = matchingOption.text;
                this.updateValue(this.value !== matchingOption.value);
                return;
            }

            if (topFilteredOption) {
                this.selectedOptions = [topFilteredOption];
                this.selectedIndex = this.options.indexOf(topFilteredOption);
                this.control.value = topFilteredOption.text;
                this.filter = topFilteredOption.text;
                this.updateValue(this.value !== topFilteredOption.value);
                return;
            }

            this.selectedIndex = -1;
            this.updateFreeformValidityState();
            return;
        }

        const newValue = this.selectedIndex > -1 ? this.firstSelectedOption?.value : typedValue;
        this.updateValue(this.value !== newValue);
    }

    /**
     * Determines if a value update should involve emitting a change event, then updates the value.
     *
     * @internal
     */
    private syncValue(): void {
        this.commitSelection();
    }

    /**
     * Calculate and apply listbox positioning based on available viewport space.
     *
     * @public
     */
    public setPositioning(): void {
        if (this.noCollapse) {
            return;
        }

        if (this.$fastController.isConnected) {
            this.cleanup = autoUpdate(this, this.listbox, async () => {
                const { middlewareData, x, y } = await computePosition(
                    this,
                    this.listbox,
                    {
                        placement: "bottom",
                        strategy: "fixed",
                        middleware: [
                            flip(),
                            size({
                                apply: ({ availableHeight, rects }) => {
                                    this.listbox.style.width = `${rects.reference.width}px`;
                                    this.listbox.style.setProperty('--listbox-available-height', `${availableHeight}px`);
                                },
                            }),
                            hide(),
                        ],
                    }
                );

                if (middlewareData.hide?.referenceHidden) {
                    this.open = false;
                    this.cleanup();
                    return;
                }

                Object.assign(this.listbox.style, {
                    position: "fixed",
                    top: "0",
                    left: "0",
                    transform: `translate(${x}px, ${y}px)`,
                });
            });
        }
    }

    /**
     * Ensure that the entire list of options is used when setting the selected property.
     *
     * @param prev - the previous list of selected options
     * @param next - the current list of selected options
     *
     * @internal
     * @remarks
     * Overrides: `Listbox.selectedOptionsChanged`
     */
    public selectedOptionsChanged(
        prev: NatGenOption[] | undefined,
        next: NatGenOption[]
    ): void {
        if (this.$fastController.isConnected) {
            this._options.forEach(o => {
                o.selected = next.includes(o);
            });
        }
    }

    /**
     * Synchronize the form-associated proxy and update the value property of the element.
     *
     * @param prev - the previous collection of slotted option elements
     * @param next - the next collection of slotted option elements
     *
     * @internal
     */
    public slottedOptionsChanged(prev: Element[] | undefined, next: Element[]): void {
        super.slottedOptionsChanged(prev, next);

        if (!this.acceptFreeform && next.length === 0 && this.value !== "") {
            this.applyValue("", false);
            this.pendingSlottedValue = "";
        }

        this.updateValue();
    }

    /**
     * Sets the value and to match the first selected option.
     *
     * @param shouldEmit - if true, the change event will be emitted
     *
     * @internal
     */
    private updateValue(shouldEmit?: boolean) {
        if (this.$fastController.isConnected) {
            const nextValue = this.firstSelectedOption?.value || (this.acceptFreeform ? this.control.value : this.options.length ? this.value : "");
            this.applyValue(nextValue, false);

            if (this.acceptFreeform || this.firstSelectedOption) {
                this.control.value = this.displayValue;
            }
        }

        this.updateFreeformValidityState();

        if (shouldEmit) {
            this.$emit("change");
        }
    }

    /**
     * @internal
     */
    private clearSelectionRange() {
        const controlValueLength = this.control.value.length;
        this.control.control.setSelectionRange(controlValueLength, controlValueLength);
    }

    private findMatchingOption(value: string): NatGenOption | undefined {
        const normalized = value?.trim().toLowerCase();

        if (!normalized) {
            return undefined;
        }

        return this._options.find(option => option.text.trim().toLowerCase() === normalized);
    }

    private findOptionByValue(value: string): NatGenOption | undefined {
        const normalized = `${value ?? ""}`;

        return this._options.find(option => option.value === normalized);
    }

    private updateFreeformValidityState(): void {
        if (!this.proxy || !this.control) {
            return;
        }

        const hasInputValue = this.control.value.trim().length > 0;
        const hasMatch = !!this.findMatchingOption(this.control.value) || this.filteredOptions.length > 0;
        const isValid = this.acceptFreeform || !hasInputValue || hasMatch;

        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.setCustomValidity(isValid ? "" : NatGenCombobox.freeformValidationMessage);
        }

        const showError = !isValid && !this.hasFocus;
        this.classList.toggle("error", showError);
        this.ariaInvalid = showError ? "true" : "false";
        this.validate();
    }
}

/**
 * Includes ARIA states and properties relating to the ARIA combobox role.
 *
 * @public
 */
export class DelegatesARIACombobox {
    /**
     * See {@link https://www.w3.org/TR/wai-aria-1.2/#aria-autocomplete} for more information.
     *
     * @public
     * @remarks
     * HTML Attribute: `aria-autocomplete`
     */
    @observable
    public ariaAutoComplete: "inline" | "list" | "both" | "none" | string | null;

    /**
     * See {@link https://www.w3.org/TR/wai-aria-1.2/#aria-controls} for more information.
     *
     * @public
     * @remarks
     * HTML Attribute: `aria-controls`
     */
    @observable
    public ariaControls: string | null;
}

/**
 * Mark internal because exporting class and interface of the same name
 * confuses API documenter.
 * TODO: https://github.com/microsoft/fast/issues/3317
 * @internal
 */
export interface DelegatesARIACombobox extends DelegatesARIAListbox {}
applyMixins(DelegatesARIACombobox, DelegatesARIAListbox);

/**
 * Mark internal because exporting class and interface of the same name
 * confuses API documenter.
 * TODO: https://github.com/microsoft/fast/issues/3317
 * @internal
 */
export interface NatGenCombobox extends StartEnd, DelegatesARIACombobox {}
applyMixins(NatGenCombobox, StartEnd, DelegatesARIACombobox);

export const natGenComboboxDefinition = NatGenCombobox.compose({
    name: `${NatGenDesignSystem.prefix}-combobox`,
    template: comboboxTemplate({
        indicator: angleDown
    }),
    styles: comboboxStyles
});
